import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import { toast }   from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Link from '@material-ui/core/Link';
import TableBody from '@material-ui/core/TableBody';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import ModalDelete from '~/components/ModalDelete';
import ManagePackages from '~/containers/Users/ManagePackages';
import Button from '~/components/Button';

import Can from '~/contexts/Can';

import DependentRow from './components/DependentRow';
import DialogAddDependent from './components/DialogAddDependent';

const useStyles = makeStyles(() => ({
  RightTab:{
    borderStyle:'outset',
    borderWidth:'thin',
    borderBottomWidth:'inherit',
    borderRightWidth:'inherit'
  },
  padding: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  link: {
    cursor: 'pointer'
  },
  paperNewAddUser: {
    textAlign: 'right',
    margin: '16px 0px'
  },
}) );

function DependentsList({ loading, user}) {
  const [dependents, setDependents] = useState([]);
  const [isLoadingDependents, setIsLoadingDependents] = useState(true);
  const [dependentDelete, setDependentDelete] = useState();
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const [isAddDependent, setIsAddDependent] = useState(false);
  const [manageDependentPackages, setManageDependentPackages] = useState();
  const classes = useStyles();
  const { t } = useTranslation();

  const fetchDependents = () => {
    Request.get(`/admin/users/${user.id}/dependents`, null, { normalizeJSON: false }).then( resp => {
      setDependents( resp.data );
      setIsLoadingDependents(false);
    }).catch( e => {
      console.error(e);
      setIsLoadingDependents( false );
    });
  };

  useEffect( () => {
    fetchDependents();
  }, [] );

  const onAddDependent = (data) => {
    setDependents(data);
  };

  if( loading || isLoadingDependents ){
    return(
      <GridContainer>
        <GridItem xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            <CircularProgress disableShrink/>
            Carregando...
          </Typography>
        </GridItem>
      </GridContainer>
    );
  }

  if(dependents.length === 0){
    return (
      <GridContainer>
        <GridItem xs={12}>
          <Typography variant="subtitle1" align="center">
            { 'Não possui dependentes vinculados' }
          </Typography>
          <Can I="new" a="Dependents">
            <Typography color="primary" align="center">
              <Link className={classes.link} onClick={ () => setIsAddDependent(true) }>
                + Adicionar dependente
              </Link>
            </Typography>
          </Can>
          <DialogAddDependent
            isOpen={isAddDependent}
            onClose={ ()=> setIsAddDependent(false) }
            onSave={onAddDependent}
            owner={user}
          />
        </GridItem>
      </GridContainer>
    );
  }

  return (
    <>
      <ModalDelete
        modal={ isPresent(dependentDelete) }
        onClose={() => {setDependentDelete(null);}}
        isLoading={isLoadingDelete}
        message="Tem certeza que deseja excluir o vínculo de dependente?"
        deleteAction={() => {
          setIsLoadingDelete(true);
          Request.del(`/admin/users/${user.id}/dependents/${dependentDelete.id}`, null, { normalizeJSON: false }).then( () => {
            setIsLoadingDelete(false);
            const newPackages = dependents.filter( p => p.id !== dependentDelete.id);
            setDependentDelete(null);
            setDependents(newPackages);
            toast.success(t('delete-toast-success'));
          }).catch( e => {
            console.error(e);
            setIsLoadingDelete(false);
          });
        }}
      />
      { manageDependentPackages &&
        <ManagePackages
          user={manageDependentPackages}
          owner={user}
          handleClose={() => {
            setManageDependentPackages(null);
            setIsLoadingDependents(true);
            fetchDependents();
          } }
          isOpen={ isPresent(manageDependentPackages) }
          loading={false}
          onUpdateAddons={ () => {} }
        />
      }
      <DialogAddDependent
        isOpen={isAddDependent}
        onClose={ ()=> setIsAddDependent(false) }
        onSave={onAddDependent}
        owner={user}
      />
      <Paper elevation={0} className={classes.paperNewAddUser}>
        <Can I="new" a="Dependents">
          <Button onClick={() => setIsAddDependent(true)} >+ Adicionar dependente</Button>
        </Can>
      </Paper>
      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="caption table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={2}>Dependente</TableCell>
              <TableCell align="center">Adicionais</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            { dependents.map( (dependent) =>
              <DependentRow
                key={dependent.id}
                dependent={dependent}
                setDependentDelete={setDependentDelete}
                setManageDependentPackages={setManageDependentPackages}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

DependentsList.propTypes={
  loading: PropTypes.bool.isRequired,
  user: PropTypes.object
};

export default React.memo(DependentsList);
